<template>
  <div class="bg-white p-4" style="border-top: 1px solid #e5e7eb">
    <!-- File attachments preview -->
    <div
      v-if="attachedFiles.length > 0"
      class="mb-3 border-b border-gray-200 pb-3"
    >
      <div class="flex flex-wrap gap-2">
        <div
          v-for="(file, index) in attachedFiles"
          :key="index"
          class="flex items-center space-x-2 bg-gray-100 rounded-lg px-3 py-2 text-sm relative"
        >
          <div class="flex-shrink-0">
            <img
              v-if="isImageFile(file)"
              :src="getFilePreview(file)"
              alt="Preview"
              class="w-8 h-8 rounded object-cover"
            />
            <svg
              v-else
              class="w-6 h-6 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
              />
            </svg>
          </div>

          <div class="flex flex-col min-w-0">
            <span class="text-gray-700 truncate max-w-32 text-xs font-medium">
              {{ file.name }}
            </span>
            <span class="text-gray-500 text-xs">
              {{ formatFileSize(file.size) }}
            </span>
          </div>

          <button
            @click="removeFile(index)"
            class="text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <form @submit.prevent="sendMessage" class="flex items-end space-x-3">
      <!-- Plus Button (Left) -->
      <div class="flex-shrink-0">
        <input
          ref="fileInput"
          type="file"
          multiple
          class="hidden"
          @change="handleFileSelect"
          accept="image/*,.pdf,.doc,.docx,.txt,.zip,.mp4,.mp3"
        />
        <button
          type="button"
          @click="$refs.fileInput.click()"
          class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
          title="Datei anhängen"
          :disabled="uploadingFiles.size > 0"
        >
          <svg
            class="w-5 h-5 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
        </button>
      </div>

      <!-- Dynamic Text Input (Middle) -->
      <div class="flex-1">
        <div class="relative">
          <textarea
            v-model="messageText"
            ref="textarea"
            rows="1"
            placeholder="Nachricht eingeben..."
            class="w-full px-4 py-2 rounded-full resize-none text-sm block"
            style="
              min-height: 40px;
              max-height: 120px;
              line-height: 1.5;
              word-wrap: break-word;
              white-space: pre-wrap;
              overflow-wrap: break-word;
              border: 1px solid #d1d5db;
              outline: none;
            "
            @keydown="handleKeydown"
            @focus="handleFocus"
            @blur="handleBlur"
            :disabled="isSending || uploadingFiles.size > 0"
          ></textarea>
        </div>
      </div>

      <!-- Send Button (Right) - Only visible when text is entered or files attached -->
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0 scale-75 translate-x-2"
        enter-to-class="opacity-100 scale-100 translate-x-0"
        leave-active-class="transition-all duration-100 ease-in"
        leave-from-class="opacity-100 scale-100 translate-x-0"
        leave-to-class="opacity-0 scale-75 translate-x-2"
      >
        <button
          v-if="hasContent && !isSending"
          type="submit"
          class="cursor-pointer flex items-center justify-center w-8 h-8 rounded-full text-white hover:opacity-90 transition-opacity flex-shrink-0"
          :style="{ backgroundColor: buttonColor }"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"
            />
          </svg>
        </button>

        <!-- Loading Spinner (when sending) -->
        <button
          v-else-if="isSending"
          type="button"
          disabled
          class="flex items-center justify-center w-8 h-8 rounded-full text-white opacity-75 flex-shrink-0"
          :style="{ backgroundColor: buttonColor }"
        >
          <svg
            class="animate-spin h-3 w-3"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </button>
      </Transition>
    </form>
  </div>
</template>

<script>
import {
  sendMessage,
  sendMessageWithFiles,
  isImageFile,
  formatFileSize,
} from "../../lib/appwriteChat";

export default {
  name: "NewChatInput",
  props: {
    styles: {
      type: Object,
      default: () => ({
        bubbleColor: "#3b82f6",
      }),
    },
    customer: {
      type: Object,
      default: null,
    },
    conversation: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      messageText: "",
      isSending: false,
      attachedFiles: [],
      uploadingFiles: new Set(),
    };
  },
  computed: {
    hasContent() {
      return (
        this.messageText.trim().length > 0 || this.attachedFiles.length > 0
      );
    },
    buttonColor() {
      return this.styles.buttonColor || "#000000";
    },
    focusRingColor() {
      return this.buttonColor;
    },
  },
  watch: {
    messageText() {
      this.adjustHeight();
    },
  },
  emits: ["message-sent"],
  methods: {
    // Handle file selection
    handleFileSelect(event) {
      const files = Array.from(event.target.files);

      // Validate files
      const validFiles = files.filter((file) => {
        // Check file size (max 10MB) - FIXED: Changed from 5MB to 10MB
        if (file.size > 10 * 1024 * 1024) {
          console.warn(`File ${file.name} is too large (max 10MB)`);
          // FIXED: German error message
          alert(
            `Die Datei "${file.name}" ist zu groß. Die maximale Dateigröße beträgt 10MB.`
          );
          return false;
        }
        return true;
      });

      if (validFiles.length > 0) {
        this.attachedFiles.push(...validFiles);

        // Clear the input so the same file can be selected again
        event.target.value = "";
      }
    },

    // Remove file from attachments
    removeFile(index) {
      // Clean up object URL if it's an image
      const file = this.attachedFiles[index];
      if (this.isImageFile(file)) {
        URL.revokeObjectURL(this.getFilePreview(file));
      }

      this.attachedFiles.splice(index, 1);
      this.uploadingFiles.delete(index);
    },

    // Check if file is an image
    isImageFile(file) {
      return file.type && file.type.startsWith("image/");
    },

    // Get file preview URL
    getFilePreview(file) {
      return URL.createObjectURL(file);
    },

    // Format file size
    formatFileSize(bytes) {
      return formatFileSize(bytes);
    },

    // Enhanced sendMessage with file support
    async sendMessage() {
      if (!this.hasContent || this.isSending) return;

      // Check if we have customer and conversation data
      if (!this.customer || !this.conversation) {
        console.error(
          "Cannot send message: missing customer or conversation data"
        );
        return;
      }

      const content = this.messageText.trim();
      const files = [...this.attachedFiles];
      this.isSending = true;

      try {
        console.log("🔄 Sending message:", {
          content,
          filesCount: files.length,
        });

        let sentMessage;

        if (files.length > 0) {
          // Send message with files
          sentMessage = await sendMessageWithFiles(
            this.conversation.$id,
            this.customer.anonymousId,
            content,
            "customer",
            files
          );
        } else {
          // Send regular message
          sentMessage = await sendMessage(
            this.conversation.$id,
            this.customer.anonymousId,
            content,
            "customer"
          );
        }

        console.log("✅ Message sent successfully:", sentMessage);

        // Emit to parent component
        this.$emit("message-sent", {
          message: sentMessage,
          type: "customer",
        });

        // Clear input and files
        this.messageText = "";
        this.attachedFiles = [];
        this.adjustHeight();

        // Clean up object URLs
        files.forEach((file) => {
          if (this.isImageFile(file)) {
            URL.revokeObjectURL(this.getFilePreview(file));
          }
        });
      } catch (error) {
        console.error("❌ Error sending message:", error);
        alert("Failed to send message. Please try again.");
      } finally {
        this.isSending = false;
      }
    },

    handleFocus() {
      const textarea = this.$refs.textarea;
      if (textarea) {
        textarea.style.borderColor = this.focusRingColor;
        textarea.style.boxShadow = `0 0 0 2px ${this.focusRingColor}40`;
      }
    },

    handleBlur() {
      const textarea = this.$refs.textarea;
      if (textarea) {
        textarea.style.borderColor = "#d1d5db";
        textarea.style.boxShadow = "none";
      }
    },

    adjustHeight() {
      this.$nextTick(() => {
        const textarea = this.$refs.textarea;
        if (textarea) {
          textarea.style.height = "auto";
          textarea.style.overflowY = "hidden";

          textarea.offsetHeight;

          const scrollHeight = textarea.scrollHeight;
          const newHeight = Math.max(40, Math.min(scrollHeight, 120));
          textarea.style.height = newHeight + "px";

          let borderRadius;
          if (newHeight <= 40) {
            borderRadius = "9999px";
          } else if (newHeight <= 60) {
            borderRadius = "16px";
          } else if (newHeight <= 80) {
            borderRadius = "12px";
          } else {
            borderRadius = "8px";
          }

          textarea.style.borderRadius = borderRadius;

          if (scrollHeight > 120) {
            textarea.style.overflowY = "auto";
          }
        }
      });
    },

    handleKeydown(event) {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        this.sendMessage();
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      if (this.$refs.textarea) {
        this.$refs.textarea.focus();
      }
    });
  },
};
</script>

<style scoped>
textarea {
  transition: height 0.1s ease-out, border-radius 0.1s ease-out,
    border-color 0.2s ease, box-shadow 0.2s ease;
  border-radius: 9999px;
  box-sizing: border-box;
  word-wrap: break-word;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 transparent;
}

textarea::-webkit-scrollbar {
  width: 4px;
}

textarea::-webkit-scrollbar-track {
  background: transparent;
}

textarea::-webkit-scrollbar-thumb {
  background-color: #cbd5e0;
  border-radius: 2px;
}

textarea::-webkit-scrollbar-thumb:hover {
  background-color: #a0aec0;
}
</style>
